import type { GetPipelineQuery } from '@/gql/graphql';
import { MutationCache, MutationKey, hashKey } from '@tanstack/react-query';

export type SquashConfig<TVariables> = {
  entityId: (variables: TVariables) => string;
  merge: (older: TVariables, newer: TVariables) => TVariables;
};

export function trySquashPreviousMutation<TVariables>(args: {
  mutationCache: MutationCache;
  mutationKey: MutationKey;
  variables: TVariables;
  squash: SquashConfig<TVariables>;
}): {
  mergedVariables: TVariables | null;
  inheritedPreviousPipelineQuery: GetPipelineQuery | undefined;
} {
  const allMutations = args.mutationCache.getAll();
  // The current mutation is already the last entry in the cache by the
  // time onMutate fires, so the previous mutation is second-to-last.
  const prev = allMutations[allMutations.length - 2];

  if (
    prev?.state.isPaused &&
    prev.options.mutationKey &&
    hashKey(prev.options.mutationKey) === hashKey(args.mutationKey) &&
    args.squash.entityId(prev.state.variables as TVariables) ===
      args.squash.entityId(args.variables)
  ) {
    const merged = args.squash.merge(
      prev.state.variables as TVariables,
      args.variables
    );

    // Inherit the older mutation's previousPipelineQuery for correct rollback
    const oldContext = prev.state.context as
      | { previousPipelineQuery?: GetPipelineQuery }
      | undefined;
    const inheritedPreviousPipelineQuery = oldContext?.previousPipelineQuery;

    args.mutationCache.remove(prev);

    return { mergedVariables: merged, inheritedPreviousPipelineQuery };
  }

  return { mergedVariables: null, inheritedPreviousPipelineQuery: undefined };
}
